/*
 * BC Outlook Add-in — Task Pane (orchestrator)
 *
 * This file bootstraps the add-in and wires modules together.
 * Business logic lives in focused modules:
 *   theme.ts, taskpane-auth.ts, companies.ts, customers.ts,
 *   email-context.ts, attachments.ts, helpers.ts
 */

/* global document, Office */
/* eslint-disable no-undef */

import { applyTheme } from "./theme";
import { setStatus, showSelectionError } from "./helpers";
import {
  ensureRuntimeConfigLoaded,
  applyRuntimeVisibility,
  loadSettings,
  saveSettings,
  clearStoredSettingsAndAuth,
  initAuth,
  signOut,
  resetAuthState,
  renderAuthDiagnostics,
  setOnSignInSuccess,
} from "./taskpane-auth";
import { loadCompanies, setOnCompanyChanged, clearCompanyLoadStatus } from "./companies";
import { loadDocTypes } from "./document-types";
import { showCompanySection, showCustomersSection } from "./helpers";
import {
  initBcSearch,
  initMapUploadAction,
  loadOrders,
  resetOrderState,
  resetOrderSearchAndSelection,
  clearMapUploadStatus,
  setOnAttachmentsChanged,
  autoSelectOrdersFromEmailSubjects,
} from "./orders";
import {
  requestSelectionRefresh,
  resetEmailContext,
  setOnSelectionRefreshed,
  setOnSelectionEmpty,
  setOnSelectionError,
} from "./email-context";
import {
  renderAttachmentsCollection,
  initFileDropzone,
  initMailboxDropzone,
  initializeCategoryMenu as initDocTypeMenu,
  initAttachmentsDegradedBanner,
} from "./attachments";

// ─────────────────────────────────────────────
// Bootstrap
// ─────────────────────────────────────────────

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {
    void bootstrapTaskpane();
  }
});

async function bootstrapTaskpane(): Promise<void> {
  applyTheme();

  

  // Wire cross-module callbacks *before* any module init that might trigger them.
  setOnSignInSuccess(async () => {
    await loadCompanies();
    await loadDocTypes();
  });
  setOnCompanyChanged(async () => {
    await loadDocTypes();
    resetOrderState();
    await loadOrders();
    renderAttachmentsCollection();
    await autoSelectOrdersFromEmailSubjects();
  });
  setOnSelectionRefreshed(() => {
    clearMapUploadStatus();
    renderAttachmentsCollection();
    setStatus("loaded");
    void (async () => {
      await loadOrders();
      await autoSelectOrdersFromEmailSubjects();
    })();
  });
  setOnSelectionEmpty(() => {
    renderAttachmentsCollection();
    void loadOrders();
    setStatus("idle");
  });
  setOnSelectionError((message) => {
    renderAttachmentsCollection();
    setStatus("idle");
    showSelectionError(message);
  });
  setOnAttachmentsChanged(() => {
    renderAttachmentsCollection();
  });

  await initSettings();
  await initAuth();
  initBcSearch();
  initMapUploadAction();
  initFileDropzone();
  initMailboxDropzone();
  initDocTypeMenu();
  initAttachmentsDegradedBanner();
  requestSelectionRefresh();

  Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, onItemChanged);

  if (Office.context.requirements.isSetSupported("Mailbox", "1.13")) {
    Office.context.mailbox.addHandlerAsync(Office.EventType.SelectedItemsChanged as any, onItemChanged); // as any: SelectedItemsChanged missing from @types/office-js Mailbox 1.13 definitions
  }
}

// ─────────────────────────────────────────────
// Settings panel
// ─────────────────────────────────────────────

async function initSettings(): Promise<void> {
  await ensureRuntimeConfigLoaded();
  applyRuntimeVisibility();
  const settings = loadSettings();

  const urlInput = document.getElementById("input-bc-url") as HTMLInputElement;
  const clientIdInput = document.getElementById("input-client-id") as HTMLInputElement;
  if (urlInput) urlInput.value = settings.bcUrl;
  if (clientIdInput) clientIdInput.value = settings.clientId;

  // Restore previously selected company into dropdown before companies are re-fetched
  if (settings.companyId) {
    const sel = document.getElementById("select-company") as HTMLSelectElement;
    if (sel) {
      const opt = document.createElement("option");
      opt.value = settings.companyId;
      opt.textContent = settings.companyName || settings.companyId;
      opt.selected = true;
      sel.appendChild(opt);
    }
    showCompanySection(true);
  }

  document.getElementById("btn-settings-toggle")?.addEventListener("click", toggleSettingsPanel);
  document.getElementById("btn-banner-signin")?.addEventListener("click", openSettingsPanel);
  document.getElementById("btn-company-load-banner-open")?.addEventListener("click", openSettingsPanel);
  document.getElementById("btn-refresh-company")?.addEventListener("click", () => {
    loadCompanies();
  });

  document.getElementById("btn-save-settings")?.addEventListener("click", () => {
    const url = (document.getElementById("input-bc-url") as HTMLInputElement)?.value ?? "";
    const clientId = (document.getElementById("input-client-id") as HTMLInputElement)?.value ?? "";
    saveSettings({ bcUrl: url, clientId, companyId: "", companyName: "", companyAcronym: "", departmentSetEntryIdFlt: "" });
    resetAuthState();
    showSaveStatus("✓ Saved");
  });

  document.getElementById("btn-clear-settings")?.addEventListener("click", () => {
    try {
      signOut();
      clearStoredSettingsAndAuth();

      const urlInput = document.getElementById("input-bc-url") as HTMLInputElement;
      const clientIdInput = document.getElementById("input-client-id") as HTMLInputElement;
      const companySelect = document.getElementById("select-company") as HTMLSelectElement;
      if (urlInput) urlInput.value = "";
      if (clientIdInput) clientIdInput.value = "";
      if (companySelect) companySelect.innerHTML = `<option value="">- select a company -</option>`;

      resetOrderState();
      resetEmailContext();

      const bcRecordsList = document.getElementById("bc-records-list");
      if (bcRecordsList) {
        bcRecordsList.innerHTML = '<div class="bc-hint">Select a company to search operational orders.</div>';
      }
      renderAttachmentsCollection();
      showCompanySection(false);
      showCustomersSection(false);
      clearCompanyLoadStatus();
      renderAuthDiagnostics();
      showSaveStatus("✓ Settings cleared");
    } catch (err: any) {
      console.error("Clear settings failed:", err);
      showSaveStatus("❌ Clear failed");
    }
  });
}

function toggleSettingsPanel(): void {
  const panel = document.getElementById("settings-panel");
  const content = document.getElementById("main-content");
  if (!panel || !content) return;
  const isOpen = panel.style.display !== "none";
  panel.style.display = isOpen ? "none" : "block";
  content.style.display = isOpen ? "flex" : "none";
  document.getElementById("btn-settings-toggle")?.classList.toggle("icon-btn--active", !isOpen);
}

function openSettingsPanel(): void {
  const panel = document.getElementById("settings-panel");
  const content = document.getElementById("main-content");
  if (!panel || !content) return;
  panel.style.display = "block";
  content.style.display = "none";
  document.getElementById("btn-settings-toggle")?.classList.add("icon-btn--active");
}

function showSaveStatus(msg: string): void {
  const el = document.getElementById("settings-save-status");
  if (!el) return;
  el.textContent = msg;
  setTimeout(() => {
    el.textContent = "";
  }, 2500);
}

// ─────────────────────────────────────────────
// Item changed
// ─────────────────────────────────────────────

function onItemChanged(): void {
  // Full reset on every email switch -- search box, search results, and the entire order
  // selection (manual and auto) -- see resetOrderSearchAndSelection. A selection made for
  // one email carrying over to a different one is a real mis-upload risk, so none of it is
  // allowed to persist across a switch. requestSelectionRefresh below re-syncs the new
  // email's own attachments, and its onSelectionRefreshed handler runs
  // autoSelectOrdersFromEmailSubjects against it -- a match gets auto-selected, no match
  // leaves the selection blank rather than falling back to anything stale.
  resetOrderSearchAndSelection();
  requestSelectionRefresh();
}
